import { ref } from 'vue'

/**
 * Runtime-configurable values injected by the Go server at /config.json.
 * Lets operators point the same pre-built SPA bundle at staging/prod by
 * flipping env vars (e.g. PORTAL_HUB_URL) instead of rebuilding Vue.
 *
 * VITE_HUB_URL is kept as a dev-only fallback so `npm run dev` against a
 * bare portal process still works without a running /config.json endpoint.
 */
export interface RuntimeConfig {
  hub_url: string
  keycloak_url: string
  admin_url: string
  billing_enabled: boolean
}

const state = ref<RuntimeConfig | null>(null)
let inflight: Promise<RuntimeConfig> | null = null

const devFallback: RuntimeConfig = {
  hub_url: import.meta.env.VITE_HUB_URL ?? '',
  keycloak_url: import.meta.env.VITE_KEYCLOAK_URL ?? '',
  admin_url: import.meta.env.VITE_ADMIN_URL ?? '',
  billing_enabled: false,
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (state.value) return state.value
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await fetch('/config.json', {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (!res.ok) throw new Error(`GET /config.json: ${res.status}`)
      const cfg = (await res.json()) as RuntimeConfig
      // Prefer server-provided value; fall back to the Vite env override
      // only when the server returned an empty string (dev without the
      // PORTAL_HUB_URL env var set).
      state.value = {
        hub_url: cfg.hub_url || devFallback.hub_url,
        keycloak_url: cfg.keycloak_url || devFallback.keycloak_url,
        admin_url: cfg.admin_url || devFallback.admin_url,
        billing_enabled: cfg.billing_enabled ?? false,
      }
      return state.value
    } catch {
      state.value = devFallback
      return state.value
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function useRuntimeConfig() {
  return state
}
